/**
 * TranslationTextModal - Collects translation text from the user.
 * Shows a dynamic title based on the selected translation language.
 * Includes character count display and minimum 5-character validation.
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ModalShell from './ModalShell';
import { TRANSLATION_LANGUAGES } from '../useResourceModalFlow';

interface TranslationTextModalProps {
  languageCode: string;
  initialText?: string;
  onSubmit: (text: string) => void;
  onBack: () => void;
  onClose: () => void;
}

const MIN_CHARS = 5;

const TranslationTextModal: React.FC<TranslationTextModalProps> = ({
  languageCode,
  initialText = '',
  onSubmit,
  onBack,
  onClose,
}) => {
  const { t } = useTranslation(['creator', 'common']);
  const [text, setText] = useState(initialText);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const charCount = text.trim().length;
  const isValid = charCount >= MIN_CHARS;

  const languageName = useMemo(() => {
    const lang = TRANSLATION_LANGUAGES.find((l) => l.code === languageCode);
    return lang ? lang.name : languageCode;
  }, [languageCode]);

  const title = t('creator:workflow.resourceModal.translationTitle', {
    language: languageName,
  });

  useEffect(() => {
    // Auto-focus the textarea on mount
    textareaRef.current?.focus();
  }, []);

  const handleContinue = () => {
    if (isValid) {
      onSubmit(text.trim());
    }
  };

  return (
    <ModalShell
      title={title}
      onClose={onClose}
      onBack={onBack}
      onNext={handleContinue}
      nextDisabled={!isValid}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('creator:workflow.stage0.translationPlaceholder')}
          style={{
            width: '100%',
            minHeight: '200px',
            padding: '14px',
            borderRadius: '10px',
            border: '1px solid var(--border-color, #d1d5db)',
            fontSize: '0.95rem',
            lineHeight: '1.6',
            resize: 'vertical',
            fontFamily: 'inherit',
            color: 'var(--text-primary, #1a1a2e)',
            background: 'var(--bg-primary, #fff)',
            transition: 'border-color 0.15s',
            outline: 'none',
            boxSizing: 'border-box',
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = 'var(--primary-color, #6366f1)';
            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)';
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = 'var(--border-color, #d1d5db)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        />
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '0.82rem',
            color: charCount > 0 && charCount < MIN_CHARS
              ? 'var(--text-warning, #d97706)'
              : 'var(--text-secondary, #6b7280)',
          }}
        >
          <span>
            {t('creator:workflow.resourceModal.charCount', { count: charCount })}
          </span>
          {charCount > 0 && charCount < MIN_CHARS && (
            <span style={{ fontStyle: 'italic' }}>
              min {MIN_CHARS}
            </span>
          )}
        </div>
      </div>
    </ModalShell>
  );
};

export default TranslationTextModal;
